import { useLayoutEffect, useRef, useState } from 'react';
import { captureAnalytics } from '../../actions/preload';

// A renewal updates Redux's signed URL before its promise resolves. Keep the
// currently displayed PDF throughout that request and download, including if
// the user starts editing while either is pending.
const useDocumentRefresh = ({
  documentUrl,
  recordId,
  fileToken,
  documentRefreshKey,
  documentSaveVersion,
  viewerOpen,
  hasUnsavedEdits,
  refreshDisabled,
  onRefreshDocument,
  onDocumentRefreshed,
  onDocumentEdited: notifyDocumentEdited,
  viewerSurface,
  fromPage,
}) => {
  // Saving can keep the same file token. The parent's guarded save advances
  // this version only when the saved PDF has no newer edits pending. Compared
  // by value: useMemo may discard its cache, so object identity is unreliable.
  const identity = JSON.stringify([recordId, fileToken, documentRefreshKey, documentSaveVersion, viewerOpen]);
  const [editedIdentity, setEditedIdentity] = useState(null);
  const refreshBlocked = hasUnsavedEdits || editedIdentity === identity;
  const latest = useRef({ identity, hasUnsavedEdits: refreshBlocked });
  useLayoutEffect(() => {
    latest.current = { identity, hasUnsavedEdits: refreshBlocked };
  }, [identity, refreshBlocked]);
  const activeRequest = useRef(null);
  const ownedBlobUrl = useRef(null);
  const [replacement, setReplacement] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const currentDocumentSource = replacement?.identity === identity
    ? replacement.documentUrl : documentUrl;

  useLayoutEffect(() => {
    // A value key recurs when the user returns to a document; edits made
    // before leaving it were discarded with the previous viewer.
    setEditedIdentity(null);
    setReplacement(null);
    setRefreshing(false);
    setError(null);
    return () => {
      activeRequest.current?.abort();
      activeRequest.current = null;
      if (ownedBlobUrl.current) URL.revokeObjectURL(ownedBlobUrl.current);
      ownedBlobUrl.current = null;
    };
  }, [identity]);

  const onDocumentEdited = () => {
    if (latest.current.identity !== identity) return;
    // Exporting an edited PDF is asynchronous. Block refresh as soon as the
    // SDK reports an edit, before the parent receives its captured blob.
    latest.current.hasUnsavedEdits = true;
    setEditedIdentity(identity);
    notifyDocumentEdited?.();
  };

  const refreshDocument = async () => {
    if (!viewerOpen || !onRefreshDocument || !documentUrl || refreshDisabled
      || latest.current.hasUnsavedEdits || activeRequest.current) return;

    captureAnalytics('viewer_document_refresh_clicked', { surface: viewerSurface, from_page: fromPage });
    const controller = new AbortController();
    activeRequest.current = controller;
    const isCurrent = () => activeRequest.current === controller
      && !controller.signal.aborted && latest.current.identity === identity;

    setReplacement({ identity, documentUrl: currentDocumentSource });
    setRefreshing(true);
    setError(null);
    try {
      const result = await onRefreshDocument({ signal: controller.signal });
      if (!isCurrent() || latest.current.hasUnsavedEdits) return;
      const signedUrl = result?.data?.signed_url;
      if (result?.error || result?.canceled || !signedUrl) throw new Error('renewal_failed');

      // OCR can replace the stored object without changing its token or signed
      // URL. Bypass both the preload cache and the browser HTTP cache. Never
      // append parameters to a signed URL: that invalidates its signature.
      const response = await fetch(signedUrl, { cache: 'no-store', signal: controller.signal });
      if (!isCurrent() || latest.current.hasUnsavedEdits) return;
      if (!response.ok) throw new Error('download_failed');
      const blob = await response.blob();
      if (!isCurrent() || latest.current.hasUnsavedEdits) return;
      if (!blob.size) throw new Error('empty_document');

      const blobUrl = URL.createObjectURL(blob);
      if (ownedBlobUrl.current) URL.revokeObjectURL(ownedBlobUrl.current);
      ownedBlobUrl.current = blobUrl;
      setReplacement({ identity, documentUrl: blobUrl });
      onDocumentRefreshed?.();
    } catch {
      // Provider errors may contain signed URLs. Show only fixed copy.
      if (isCurrent()) setError('Unable to refresh the document. Try again.');
    } finally {
      if (isCurrent()) {
        activeRequest.current = null;
        setRefreshing(false);
      }
    }
  };

  return {
    currentDocumentSource,
    refreshDocument,
    refreshing,
    error,
    refreshBlocked,
    onDocumentEdited,
    // A refresh pins the displayed source, including while pending or after failure.
    // A parent URL renewal cannot reload that source; its error must not be deferred.
    canReloadWithRenewedUrl: replacement?.identity !== identity,
  };
};

export default useDocumentRefresh;
